/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { Button } from '@coursera/cds-core';

import Alert from 'bundles/authentication-common/components/Alert';
import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import Form from 'bundles/authentication-common/components/Form';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import SSOEnforcementBanner from 'bundles/authentication-common/components/SSOEnforcementBanner';
import Submit from 'bundles/authentication-common/components/Submit';
import InvisibleRecaptchaContainer from 'bundles/authentication-common/components/botManager/InvisibleRecaptchaContainer';
import { commonStyles } from 'bundles/authentication-common/components/commonStyles';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import TwoFactorAuthInput from 'bundles/authentication-common/components/inputs/TwoFactorAuthInput';
import InvalidDomainSSOEnforcementBanner from 'bundles/authentication-common/components/sso/InvalidDomainSSOEnforcementBanner';
import { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import {
  AUTHENTICATION_FORM_VARIANTS,
  type AUTHENTICATION_FORM_VARIANTS_KEY,
  SSO_BANNER_NAMES,
} from 'bundles/authentication-common/constants';
import { LOGIN_REDESIGN_RECAPTCHA_ID } from 'bundles/authentication-common/constants/botManager';
import { generalErrorMessage, getLtiLoginErrorMessages } from 'bundles/authentication-common/constants/errors';
import useEmailPasswordLogin from 'bundles/authentication-common/hooks/useEmailPasswordLogin';
import type { LoginSharedProps, LoginValues } from 'bundles/authentication-common/types';
import { isLtiInvalidDomainSSOError } from 'bundles/authentication-common/utils/ssoUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import thirdPartyAuth, { SSOTypes } from 'bundles/third-party-auth/lib';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);
const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = {
  form: css`
    /* This is an offset of the marginTop below to keep the space above consistent */
    margin-top: -12px;
  `,
  inputContainer: css`
    margin-top: var(--cds-spacing-150);
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
  `,
  experiment: css`
    display: flex;
    justify-content: space-between;
  `,
  item: css`
    :nth-child(1) {
      -ms-grid-row: 1;
    }

    :nth-child(2) {
      -ms-grid-row: 2;
    }

    :nth-child(3) {
      -ms-grid-row: 3;
    }

    :nth-child(4) {
      -ms-grid-row: 4;
    }

    :nth-child(5) {
      -ms-grid-row: 5;
    }
  `,
  submit: css`
    margin-top: 0;
  `,
  styleConflictInputFix: css`
    :nth-of-type(n) input {
      border: 0 !important;
      height: unset;
      margin-bottom: 0 !important;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
      line-height: 22px;
      font-size: 16px;
      outline: none;
      box-shadow: none !important;
      color: unset;
    }

    :nth-of-type(n) input:focus:required:invalid:focus {
      color: unset;
    }

    :nth-of-type(n) input::placeholder {
      color: var(--cds-color-neutral-primary-weak);
      opacity: 1;
    }

    /* Internet Explorer 10-11 */
    :nth-of-type(n) input:-ms-input-placeholder {
      color: var(--cds-color-neutral-primary-weak);
      opacity: 1;
    }

    /* Edge */
    :nth-of-type(n) input:-webkit-input-placeholder {
      color: var(--cds-color-neutral-primary-weak);
      opacity: 1;
    }
  `,
};

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type Context = {
  _eventData: EventData;
};

type Props = LoginSharedProps & {
  showProgressiveAuth?: boolean;
  formVariant?: AUTHENTICATION_FORM_VARIANTS_KEY;
};

export const INPUT_DEBOUNCE_TIME = 300;

const LoginForm = (props: Props, { _eventData }: Context) => {
  const { navigate, arkoseBotManagerContext, enterpriseSsoProps, showProgressiveAuth, formVariant, onEmailChange } =
    props;
  const {
    ssoError,
    isEnterpriseSsoLogin,
    isLoading,
    isRedirecting,
    onEmailChange: onEnterpriseSsoEmailChange,
    resetSsoError,
  } = enterpriseSsoProps || {};
  const [emailValue, setEmailValue] = useState(props.defaultEmail || '');
  const input = useRef<HTMLInputElement>(null);

  const showOnlyEmailInput = formVariant === AUTHENTICATION_FORM_VARIANTS.SOCIALSSO;
  const hidePasswordInput = showOnlyEmailInput || isEnterpriseSsoLogin;

  useEffect(() => {
    if (!(ssoError instanceof InvalidDomainSSOError) && ssoError?.response?.prefillEmail) {
      setEmailValue(ssoError.response.prefillEmail);
      // Call both email change handlers
      onEnterpriseSsoEmailChange?.(ssoError.response.prefillEmail);
      onEmailChange?.(ssoError.response.prefillEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError?.response?.prefillEmail]);

  const { login, error, isRunning, needsTwoFactorAuth, setError, setIsRunning, trackLoginClick, handleError } =
    useEmailPasswordLogin({
      arkoseBotManagerContext,
      eventData: _eventData,
      onSuccess: props.onSuccess,
      onError: (response) => props.onError(response as string | undefined),
      onInvalidCredential: () => input.current?.focus(),
    });

  const loginWithLtiThirdPartyAuth = async (invalidDomainSsoError: InvalidDomainSSOError, values: LoginValues) => {
    const { onSuccess } = props;
    if (!invalidDomainSsoError.token) {
      trackLoginClick('invalid');
      return setError(generalErrorMessage());
    }

    setIsRunning(true);
    trackAuthAttempts(_eventData, 'login');

    try {
      const response = await thirdPartyAuth.connect(SSOTypes.LTI, {
        token: invalidDomainSsoError.token,
        email: values.email,
        password: values.password,
        forceThirdPartyLink: true,
      });
      trackLoginClick('valid');
      return onSuccess(response?.returnTo as string);
    } catch (connectError) {
      return handleError({ responseJSON: { errorCode: connectError?.code } }, getLtiLoginErrorMessages());
    }
  };

  const handleFormSubmit = (values: Record<string, string>) => {
    const loginValues = values as LoginValues;
    if (isEnterpriseSsoLogin && enterpriseSsoProps?.onSubmit) {
      return enterpriseSsoProps.onSubmit();
    }
    if (isLtiInvalidDomainSSOError(ssoError)) {
      return loginWithLtiThirdPartyAuth(ssoError, loginValues);
    }
    return login(loginValues);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetSsoError?.(); // Clear the previous sso error when the email changes
    setEmailValue(e.target.value);
    // Call enterprise SSO email change handler (for domain checking)
    onEnterpriseSsoEmailChange?.(e.target.value);
    // Call general email change handler (for magic link tracking, etc.)
    onEmailChange?.(e.target.value);
  };

  const getSsoError = () => {
    // If we are enforcing sso because user tried to login with social methods, show this error
    if (isLoading) {
      return null;
    }
    // non-enterprise sso errors
    if (ssoError && !isEnterpriseSsoLogin) {
      const { code } = ssoError;
      if (code === 'loginDisabledForDomain') {
        return (
          <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.SOCIAL_LOGIN} css={styles.item}>
            {_t(
              'It seems you tried to log in using social login. This email is associated with an account that requires email and password authentication.'
            )}
          </SSOEnforcementBanner>
        );
      }
      if (ssoError instanceof InvalidDomainSSOError) {
        return <InvalidDomainSSOEnforcementBanner navigate={navigate} ssoError={ssoError} css={styles.item} />;
      }

      // enterprise sso errors
    } else if (isEnterpriseSsoLogin) {
      if (ssoError instanceof InvalidDomainSSOError) {
        return <InvalidDomainSSOEnforcementBanner navigate={navigate} ssoError={ssoError} css={styles.item} />;
      }
      if (ssoError?.code === 'enforceSsoV2') {
        return (
          <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.SOCIAL_LOGIN} css={styles.item}>
            {_t(
              'It seems you tried to log in using social login. This email is associated with an account that requires SSO.'
            )}
          </SSOEnforcementBanner>
        );
        // If its an enterprise login because of username and password auth disabled, show this error
      } else {
        return (
          <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.PASSWORD_LOGIN} css={styles.item}>
            {showProgressiveAuth
              ? _t('This email is linked to an account that requires SSO. Please continue with SSO.')
              : _t(
                  'It seems you tried to log in using username and password. This email is associated with an account that requires SSO.'
                )}
          </SSOEnforcementBanner>
        );
      }
    }

    return null;
  };

  const renderSubmitButton = () => {
    if (showOnlyEmailInput) {
      return null;
    }
    if (isEnterpriseSsoLogin) {
      return (
        <Button disabled={isLoading} loading={isRedirecting} type="submit" style={{ minWidth: '100%' }}>
          {_t('Continue with SSO')}
        </Button>
      );
    }
    return (
      <TrackedSubmit
        css={[styles.submit, styles.item]}
        data-e2e="login-form-submit-button"
        disabled={isRunning}
        trackingName="login_form_submit_button"
      >
        {showProgressiveAuth ? _t('Next') : _t('Login')}
      </TrackedSubmit>
    );
  };

  const { onForgotPassword } = props;

  return (
    <Form className="rc-LoginForm" css={styles.form} name="login" onSubmit={handleFormSubmit}>
      <div aria-live="assertive">
        {error && !isRunning && !isEnterpriseSsoLogin && (
          <Alert css={styles.item} type="error">
            {error}
          </Alert>
        )}
        {getSsoError()}
      </div>

      <div css={styles.inputContainer}>
        <EmailInput
          css={[styles.styleConflictInputFix, styles.item, commonStyles.emailInputReadOnly]}
          autoFocus
          data-e2e="login-email-input"
          value={emailValue}
          ref={input}
          onChange={handleEmailChange}
          isReadOnly={!!props.defaultEmail}
        />
        {!hidePasswordInput && (
          <PasswordInput
            css={[styles.styleConflictInputFix, styles.item]}
            data-e2e="login-password-input"
            hint={
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'forgot_password' });
                    onForgotPassword();
                  };
                  return (
                    <TrackedLinkButton onClick={handleOnClick} trackingName="login_form_forgot_password_link">
                      {_t('Forgot password?')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            }
            placeholder={_t('Enter your password')}
            autoComplete="current-password"
          />
        )}
        {needsTwoFactorAuth && (
          <TwoFactorAuthInput
            css={[styles.styleConflictInputFix, styles.item]}
            autoFocus
            data-e2e="verification-code-field"
          />
        )}
        <InvisibleRecaptchaContainer id={LOGIN_REDESIGN_RECAPTCHA_ID} />
        {renderSubmitButton()}
      </div>
    </Form>
  );
};

LoginForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'login',
  },
}))(LoginForm);
